import React, { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    FormControlLabel,
    IconButton,
    Radio,
    RadioGroup,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import KeyIcon from '@mui/icons-material/VpnKey';
import DataSourceService from '../../services/DataSourceService';

const monoSx = {
    fontFamily: 'monospace',
    fontSize: '0.75rem',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    color: 'var(--wb-text)',
    backgroundColor: 'var(--wb-bg-surface)',
    border: '1px solid var(--wb-border-20)',
    borderRadius: 1,
    p: 1.5,
    maxHeight: 260,
    overflow: 'auto'
};

const CopyBlock = ({ label, value }) => {
    const [copied, setCopied] = useState(false);
    const copy = async () => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {
            // Sin permiso de portapapeles: el usuario puede seleccionar el texto.
        }
    };
    return (
        <Box sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="caption" sx={{ color: 'var(--wb-text-soft)' }}>{label}</Typography>
                <Tooltip title={copied ? 'Copiado' : 'Copiar'}>
                    <IconButton size="small" onClick={copy} aria-label={`Copiar ${label}`} sx={{ color: 'var(--wb-blue)' }}>
                        <ContentCopyIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
            <Box component="pre" sx={{ ...monoSx, m: 0 }}>{value}</Box>
        </Box>
    );
};

/**
 * Campos de una conexión Snowflake. Snowflake bloquea la contraseña para
 * usuarios de servicio, así que la conexión usa SIEMPRE par de claves:
 *   - "generate" (recomendado): WhisperBI genera el par; la clave privada
 *     queda cifrada en el backend (llega acá solo como token opaco) y se
 *     muestra la pública + el SQL para crear el usuario de servicio.
 *   - "upload": el cliente sube su propia clave privada (PEM).
 *   - "keep" (edición): conserva la clave actual; desde acá se puede rotar
 *     (clave nueva en RSA_PUBLIC_KEY_2, probarla y guardar).
 * Los valores viajan por `handleInputChange` del formulario contenedor.
 */
const SnowflakeConnectionFields = ({ formData, formErrors = {}, handleInputChange, isEditMode, textFieldStyle, connectionId }) => {
    const [generating, setGenerating] = useState(false);
    const [generated, setGenerated] = useState(null);
    const [genError, setGenError] = useState(null);
    const [candidateResult, setCandidateResult] = useState(null);
    const [testingCandidate, setTestingCandidate] = useState(false);

    const set = (name, value) => handleInputChange({ target: { name, value } });
    const mode = formData.snowflakeKeyMode || (isEditMode && formData.keyFingerprint ? 'keep' : 'generate');

    const changeMode = (value) => {
        set('snowflakeKeyMode', value);
        set('snowflakeKeyToken', '');
        set('privateKeyPem', '');
        set('privateKeyPassphrase', '');
        setGenerated(null);
        setCandidateResult(null);
    };

    const generate = async () => {
        setGenerating(true);
        setGenError(null);
        setCandidateResult(null);
        try {
            const result = await DataSourceService.generateSnowflakeKeyPair({
                username: formData.username || undefined,
                role: formData.role || undefined,
                warehouse: formData.warehouse || undefined,
                databaseName: formData.database || undefined,
                keySlot: isEditMode && formData.keyFingerprint ? 2 : 1
            });
            setGenerated(result);
            set('snowflakeKeyToken', result.generated_key_token);
        } catch (err) {
            setGenError(err.message || 'No fue posible generar el par de claves');
        } finally {
            setGenerating(false);
        }
    };

    const testCandidate = async () => {
        setTestingCandidate(true);
        setCandidateResult(await DataSourceService.testSnowflakeCandidateKey(connectionId, formData.snowflakeKeyToken));
        setTestingCandidate(false);
    };

    const field = (name, label, { required = true, helper = '', placeholder = '' } = {}) => (
        <TextField
            required={required}
            margin="dense"
            name={name}
            label={label}
            fullWidth
            variant="outlined"
            value={formData[name] || ''}
            onChange={handleInputChange}
            placeholder={placeholder}
            error={Boolean(formErrors[name])}
            helperText={formErrors[name] ? `${label} es obligatorio` : helper}
            FormHelperTextProps={{ sx: { color: formErrors[name] ? 'var(--wb-danger-strong)' : 'var(--wb-muted-80)' } }}
            sx={textFieldStyle}
        />
    );

    const rotating = isEditMode && formData.keyFingerprint;

    return (
        <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
                Snowflake bloquea la contraseña para usuarios de servicio. WhisperBI se conecta con un
                <strong> usuario de servicio (TYPE = SERVICE) y par de claves</strong>, con un rol de solo lectura.
            </Alert>

            {field('host', 'Identificador de cuenta', {
                helper: 'ORGANIZACION-CUENTA (Snowsight › Admin › Accounts) o la URL https://<cuenta>.snowflakecomputing.com',
                placeholder: 'MIORG-MICUENTA'
            })}
            {field('username', 'Usuario de servicio', { placeholder: 'WHISPERBI_SVC' })}
            {field('database', 'Base de datos', { placeholder: 'VENTAS' })}
            {field('warehouse', 'Warehouse', { helper: 'El rol debe tener USAGE sobre este warehouse', placeholder: 'COMPUTE_WH' })}
            {field('role', 'Rol', { helper: 'Rol de solo lectura asignado al usuario de servicio', placeholder: 'WHISPERBI_READER' })}
            {field('schema', 'Schema (opcional)', { required: false, placeholder: 'PUBLIC' })}

            <Box sx={{ mt: 2, p: 2, border: '1px solid var(--wb-border-20)', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <KeyIcon sx={{ color: 'var(--wb-blue)' }} />
                    <Typography sx={{ fontWeight: 600 }}>Autenticación: par de claves</Typography>
                </Box>
                {isEditMode && formData.keyFingerprint && (
                    <Typography variant="body2" sx={{ color: 'var(--wb-text-soft)', mb: 1, wordBreak: 'break-all' }}>
                        Clave actual: <code>{formData.keyFingerprint}</code>
                    </Typography>
                )}

                <RadioGroup row value={mode} onChange={(e) => changeMode(e.target.value)}>
                    {isEditMode && formData.keyFingerprint && (
                        <FormControlLabel value="keep" control={<Radio size="small" />} label="Mantener clave actual" />
                    )}
                    <FormControlLabel
                        value="generate"
                        control={<Radio size="small" />}
                        label={rotating ? 'Rotar: generar clave nueva' : 'Generar par de claves (recomendado)'}
                    />
                    <FormControlLabel value="upload" control={<Radio size="small" />} label="Subir mi clave privada" />
                </RadioGroup>

                {mode === 'generate' && (
                    <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" sx={{ color: 'var(--wb-text-soft)', mb: 1 }}>
                            WhisperBI genera la clave y guarda la privada cifrada; tú solo registras la clave pública en
                            Snowflake con el script de abajo. Completa usuario, warehouse, rol y base para que el script
                            salga listo para ejecutar.
                        </Typography>
                        <Button variant="outlined" onClick={generate} disabled={generating}
                            startIcon={generating ? <CircularProgress size={16} /> : <KeyIcon />}>
                            {generated ? 'Generar otro par' : 'Generar par de claves'}
                        </Button>
                        {formErrors.snowflakeKey && !generated && (
                            <Typography variant="caption" sx={{ display: 'block', color: 'var(--wb-danger-strong)', mt: 1 }}>
                                Genera un par de claves o sube tu clave privada.
                            </Typography>
                        )}
                        {genError && <Alert severity="error" sx={{ mt: 1 }}>{genError}</Alert>}
                        {generated && (
                            <Box sx={{ mt: 2 }}>
                                <CopyBlock label="Huella (debe coincidir con RSA_PUBLIC_KEY_FP de DESC USER)" value={generated.fingerprint} />
                                <CopyBlock label="Clave pública" value={generated.public_key} />
                                <CopyBlock
                                    label={rotating ? 'SQL de rotación (ejecútalo en Snowflake)' : 'SQL para crear el usuario de servicio (ejecútalo en Snowflake)'}
                                    value={generated.setup_sql}
                                />
                                <Alert severity="warning">
                                    {rotating
                                        ? 'Ejecuta el SQL, prueba la clave nueva y guarda la conexión para activarla. Después retira la clave anterior (UNSET RSA_PUBLIC_KEY).'
                                        : 'Ejecuta el SQL en Snowflake y después usa "Probar conexión".'}
                                </Alert>
                                {rotating && connectionId && (
                                    <Box sx={{ mt: 1.5 }}>
                                        <Button variant="outlined" onClick={testCandidate} disabled={testingCandidate}
                                            startIcon={testingCandidate ? <CircularProgress size={16} /> : null}>
                                            Probar clave nueva
                                        </Button>
                                        {candidateResult && (
                                            <Alert severity={candidateResult.success ? 'success' : 'error'} sx={{ mt: 1 }}>
                                                {candidateResult.message}
                                            </Alert>
                                        )}
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>
                )}

                {mode === 'upload' && (
                    <Box sx={{ mt: 1 }}>
                        <TextField
                            margin="dense"
                            name="privateKeyPem"
                            label="Clave privada (PEM)"
                            placeholder="-----BEGIN PRIVATE KEY-----"
                            multiline
                            minRows={4}
                            fullWidth
                            value={formData.privateKeyPem || ''}
                            onChange={handleInputChange}
                            error={Boolean(formErrors.snowflakeKey)}
                            helperText={formErrors.snowflakeKey ? 'Pega tu clave privada' : 'RSA de al menos 2048 bits; se guarda cifrada'}
                            FormHelperTextProps={{ sx: { color: formErrors.snowflakeKey ? 'var(--wb-danger-strong)' : 'var(--wb-muted-80)' } }}
                            sx={{ ...textFieldStyle, '& textarea': { fontFamily: 'monospace', fontSize: '0.75rem' } }}
                        />
                        <TextField
                            margin="dense"
                            name="privateKeyPassphrase"
                            label="Passphrase (si la clave está cifrada)"
                            type="password"
                            fullWidth
                            value={formData.privateKeyPassphrase || ''}
                            onChange={handleInputChange}
                            sx={textFieldStyle}
                        />
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default SnowflakeConnectionFields;
